import { View, Text, StyleSheet, Pressable } from 'react-native';
import React, { useEffect, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { XIcon } from '@heroicons/react/solid';
import Toast from 'react-native-toast-message';
import { Button } from '@ologyhealth/wp-components';

import BasicLayout from '../components/Shared/BasicLayout';
import Constants from '../constants';
import { Navigate } from '../react-router';
import { UserContext, WpUserAction } from '../contexts/user';
import Dropdown from '../components/Dropdown';
import Input, { Label, Info } from '../components/Input';
import LeadDropdown from '../components/Leads/LeadDropdown';
import GroupDropdown from '../components/GroupDropdown';
import MessageEditor from '../components/MessageEditor';
import Checkbox from '../components/Checkbox';
import Spinner from '../components/Spinner';
import WpModal from '../components/WpModal';
import ModalBody from '../components/ModalBody';
import ModalFooter from '../components/ModalFooter';
import BillingModal from '../components/User/BillingModal';
import { Button as LocalButton, Theme } from '../components/Button';
import { MessageBubble } from '../components/Shared/MessageFeed';
import { CREATE_LEAD, EMAIL_LEADS, MESSAGE_LEADS } from '../graphql/lead';
import { GET_LEADS } from '../graphql/queries';
import { UPDATE_USER } from '../graphql/user';

type Channel = 'sms' | 'email';

const CHANNEL_ITEMS = [
  { label: 'Text Message', value: 'sms' },
  { label: 'Email', value: 'email' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digitsOnly(value: string): string {
  return (value || '').replace(/\D/g, '');
}

function normalizePhone(raw: string): string | null {
  const digits = digitsOnly(raw);
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  if (digits.length >= 10) {
    return digits;
  }
  return null;
}

function splitExtraInput(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseExtraPieces(channel: Channel, raw: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  splitExtraInput(raw).forEach((piece) => {
    if (channel === 'sms') {
      const phone = normalizePhone(piece);
      if (!phone) {
        invalid.push(piece);
        return;
      }
      if (valid.indexOf(phone) === -1) {
        valid.push(phone);
      }
      return;
    }
    const email = piece.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      invalid.push(piece);
      return;
    }
    if (valid.indexOf(email) === -1) {
      valid.push(email);
    }
  });
  return { valid, invalid };
}

function mergeUnique(existing: string[], incoming: string[]): string[] {
  const next = [...existing];
  incoming.forEach((value) => {
    if (next.indexOf(value) === -1) {
      next.push(value);
    }
  });
  return next;
}

export default function Compose(): JSX.Element {
  const { state, dispatch } = React.useContext(UserContext);

  const [channel, setChannel] = useState<Channel>('sms');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [extraPhones, setExtraPhones] = useState<string[]>([]);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [extraDraft, setExtraDraft] = useState('');

  const [message, setMessage] = useState<string>();
  const [messageContent, setMessageContent] = useState<object>();
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isBilling, setIsBilling] = useState(false);
  const [understandTiming, setUnderstandTiming] = useState(false);
  const [understandContacts, setUnderstandContacts] = useState(false);
  const [understandEdit, setUnderstandEdit] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const isSuperAdmin = !!state.user?.wpAdmin;
  const draftExtras = parseExtraPieces(channel, extraDraft);
  const resolvedPhones = mergeUnique(extraPhones, channel === 'sms' ? draftExtras.valid : []);
  const resolvedEmails = mergeUnique(extraEmails, channel === 'email' ? draftExtras.valid : []);
  const committedExtras = channel === 'sms' ? extraPhones : extraEmails;
  const leadLabel = Constants.localizations.lead;
  const leadsLabel = Constants.localizations.leads;
  const channelItems = isSuperAdmin
    ? CHANNEL_ITEMS
    : CHANNEL_ITEMS.filter((item) => item.value === 'sms');

  const [messageLeads] = useMutation(MESSAGE_LEADS, { fetchPolicy: 'no-cache' });
  const [emailLeads] = useMutation(EMAIL_LEADS, { fetchPolicy: 'no-cache' });
  const [createLead] = useMutation(CREATE_LEAD, { fetchPolicy: 'no-cache' });
  const [getLeads] = useLazyQuery(GET_LEADS, { fetchPolicy: 'no-cache' });
  const [updateUser, updateUserData] = useMutation(UPDATE_USER, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (updateUserData.data?.updateUser?.user?.organization) {
      dispatch({
        type: WpUserAction.REFRESH_ORGANIZATION,
        organization: updateUserData.data.updateUser.user.organization,
      });
      setIsBilling(false);
    }
  }, [updateUserData.data, dispatch]);

  useEffect(() => {
    if (!isSuperAdmin && channel === 'email') {
      setChannel('sms');
    }
  }, [isSuperAdmin, channel]);

  const hasRecipients =
    selectedLeads.length > 0 || selectedGroups.length > 0 ||
    (channel === 'sms' ? resolvedPhones.length > 0 : resolvedEmails.length > 0);

  const smsReady = !!messageContent && !!message?.trim();
  const emailReady = !!emailSubject.trim() && !!emailBody.trim();
  const canSend =
    hasRecipients &&
    (channel === 'sms' ? smsReady : isSuperAdmin && emailReady) &&
    !loading;

  const recipientSummary = () => {
    const parts: string[] = [];
    if (selectedLeads.length) {
      parts.push(`${selectedLeads.length} ${selectedLeads.length === 1 ? leadLabel : leadsLabel}`);
    }
    if (selectedGroups.length) {
      parts.push(`${selectedGroups.length} group${selectedGroups.length === 1 ? '' : 's'}`);
    }
    if (resolvedPhones.length && channel === 'sms') {
      parts.push(`${resolvedPhones.length} extra number${resolvedPhones.length === 1 ? '' : 's'}`);
    }
    if (resolvedEmails.length && channel === 'email') {
      parts.push(`${resolvedEmails.length} extra email${resolvedEmails.length === 1 ? '' : 's'}`);
    }
    if (parts.length) {
      return `Sending to ${parts.join(', ')}`;
    }
    if (extraDraft.trim() && draftExtras.invalid.length) {
      return channel === 'email' ? 'Enter a valid email address' : 'Enter a valid phone number';
    }
    return 'Add at least one recipient';
  };

  const addExtraRecipients = () => {
    const pieces = splitExtraInput(extraDraft);
    if (!pieces.length) {
      return;
    }

    if (channel === 'sms') {
      const next = [...extraPhones];
      const invalid: string[] = [];
      pieces.forEach((piece) => {
        const phone = normalizePhone(piece);
        if (!phone) {
          invalid.push(piece);
          return;
        }
        if (next.indexOf(phone) === -1) {
          next.push(phone);
        }
      });
      setExtraPhones(next);
      if (invalid.length) {
        Toast.show({
          type: 'error',
          text1: 'Invalid phone number',
          text2: `Could not add: ${invalid.join(', ')}`,
        });
      }
    } else {
      const next = [...extraEmails];
      const invalid: string[] = [];
      pieces.forEach((piece) => {
        const email = piece.trim().toLowerCase();
        if (!EMAIL_PATTERN.test(email)) {
          invalid.push(piece);
          return;
        }
        if (next.indexOf(email) === -1) {
          next.push(email);
        }
      });
      setExtraEmails(next);
      if (invalid.length) {
        Toast.show({
          type: 'error',
          text1: 'Invalid email',
          text2: `Could not add: ${invalid.join(', ')}`,
        });
      }
    }
    setExtraDraft('');
  };

  const removeExtraRecipient = (value: string) => {
    if (channel === 'sms') {
      setExtraPhones(extraPhones.filter((phone) => phone !== value));
    } else {
      setExtraEmails(extraEmails.filter((email) => email !== value));
    }
  };

  const resetForm = () => {
    setSelectedLeads([]);
    setSelectedGroups([]);
    setExtraPhones([]);
    setExtraEmails([]);
    setExtraDraft('');
    setMessage(undefined);
    setMessageContent(undefined);
    setEmailSubject('');
    setEmailBody('');
    setUnderstandTiming(false);
    setUnderstandContacts(false);
    setUnderstandEdit(false);
    setFormKey((key) => key + 1);
  };

  const mutationFailed = (payload?: { success?: boolean; errors?: string[] } | null, fallback?: string) => {
    if (!payload) {
      return fallback || 'Something went wrong. Please try again.';
    }
    if (payload.success === false) {
      return payload.errors?.[0] || fallback || 'Something went wrong. Please try again.';
    }
    return null;
  };

  const resolveExtraPhones = async (phones: string[]): Promise<string[]> => {
    const ids: string[] = [];
    for (const phone of phones) {
      try {
        const created = await createLead({
          variables: {
            lead: {
              name: phone,
              phone,
            },
          },
        });
        const newId = created.data?.createLead?.lead?.id;
        if (created.data?.createLead?.success && newId) {
          ids.push(newId);
          continue;
        }
      } catch (_err) {
        // Fall through to a search in case this number is already a contact.
      }

      try {
        const found = await getLeads({ variables: { page: 1, search: phone } });
        const match = found.data?.leads?.leads?.find((lead: { id: string; phone?: string }) => {
          return digitsOnly(lead.phone || '') === digitsOnly(phone);
        });
        if (match?.id) {
          ids.push(match.id);
        }
      } catch (_err) {
        // Leave this number off the send list; the caller toasts if none resolve.
      }
    }
    return ids;
  };

  const sendSms = async () => {
    let extraIds: string[] = [];
    if (resolvedPhones.length) {
      extraIds = await resolveExtraPhones(resolvedPhones);
      if (extraIds.length < resolvedPhones.length) {
        Toast.show({
          type: 'error',
          text1: 'Some numbers could not be added',
          text2: 'We will still send to the contacts we could resolve.',
        });
      }
    }

    const leadIds = [...selectedLeads, ...extraIds];
    if (!leadIds.length && !selectedGroups.length) {
      throw new Error(`Add ${leadsLabel}, groups, or phone numbers before sending.`);
    }

    const result = await messageLeads({
      variables: {
        leadIds: leadIds.length ? leadIds : undefined,
        groupIds: selectedGroups.length ? selectedGroups : undefined,
        messageContent,
      },
    });

    const fail = mutationFailed(result.data?.messageLeads, 'Your texts could not be sent.');
    if (fail) {
      throw new Error(fail);
    }
  };

  const sendEmail = async () => {
    if (!isSuperAdmin) {
      throw new Error('Email sending is only available to super admins.');
    }
    const result = await emailLeads({
      variables: {
        leadIds: selectedLeads.length ? selectedLeads : undefined,
        groupIds: selectedGroups.length ? selectedGroups : undefined,
        emails: resolvedEmails.length ? resolvedEmails : undefined,
        subject: emailSubject.trim(),
        body: emailBody.trim(),
      },
    });

    const fail = mutationFailed(
      result.data?.emailLeads,
      'Email sending is not available on the server yet.'
    );
    if (fail) {
      throw new Error(fail);
    }
  };

  const performSend = async () => {
    setError(undefined);
    setLoading(true);
    if (draftExtras.invalid.length) {
      Toast.show({
        type: 'error',
        text1: channel === 'email' ? 'Invalid email' : 'Invalid phone number',
        text2: `Skipped: ${draftExtras.invalid.join(', ')}`,
      });
    }
    try {
      if (channel === 'sms') {
        await sendSms();
        Toast.show({
          type: 'success',
          text1: 'Success',
          text2: 'Your messages are now being sent out in the background.',
        });
      } else {
        await sendEmail();
        Toast.show({
          type: 'success',
          text1: 'Success',
          text2: 'Your emails are now being sent out in the background.',
        });
      }
      resetForm();
      setShowConfirm(false);
    } catch (err: any) {
      const graphMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message ||
        err?.message;
      const isMissingEmailApi =
        channel === 'email' &&
        typeof graphMessage === 'string' &&
        /emailLeads|Cannot query field/i.test(graphMessage);
      const text = isMissingEmailApi
        ? 'Email sending is not on the API yet. Share compose-email-backend-notes.txt with the backend team.'
        : graphMessage || 'Something went wrong. Please try again.';
      setError(text);
      Toast.show({
        type: 'error',
        text1: channel === 'email' ? 'Email not sent' : 'Message not sent',
        text2: text,
      });
    } finally {
      setLoading(false);
    }
  };

  const needsSmsBilling = channel === 'sms' && !state.user?.organization?.billing?.active;
  const needsBlastConfirm =
    channel === 'sms' &&
    Constants.blastView &&
    (!state.user?.organization?.understandsTiming || resolvedPhones.length > 0);

  const onSend = () => {
    if (!canSend) {
      return;
    }
    if (needsSmsBilling) {
      setIsBilling(true);
      return;
    }
    if (needsBlastConfirm) {
      setShowConfirm(true);
      return;
    }
    performSend();
  };

  const confirmDisabled =
    Constants.blastView && (!understandContacts || !understandEdit || !understandTiming);

  const onCompleteBilling = (token?: string, plan?: string) => {
    if (state.user) {
      updateUser({
        variables: {
          userId: state.user.id,
          user: {
            token,
            plan,
          },
        },
      });
    }
  };

  const renderChips = () => {
    if (!committedExtras.length) {
      return null;
    }
    return (
      <View style={styles.chipRow}>
        {committedExtras.map((value) => (
          <View key={value} style={styles.chip}>
            <Text style={styles.chipText}>{value}</Text>
            <Pressable onPress={() => removeExtraRecipient(value)} hitSlop={8}>
              <XIcon style={cssStyles.chipIcon} />
            </Pressable>
          </View>
        ))}
      </View>
    );
  };

  return (
    <BasicLayout active="/compose">
      {state.user && !isSuperAdmin ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <>
        <View style={styles.container}>
        <Text style={styles.heading}>Compose</Text>
        <Text style={styles.subtitle}>
          {isSuperAdmin
            ? `Send a mass text or email to existing ${leadsLabel}, groups, or extra recipients who are not already in your list.`
            : `Send a mass text to existing ${leadsLabel}, groups, or extra phone numbers who are not already in your list.`}
        </Text>

        <View style={styles.card}>
          {isSuperAdmin ? (
            <View style={[styles.section, { zIndex: 6 }]}>
              <Label name="Channel" />
              <Dropdown
                placeholder="Select channel"
                items={channelItems}
                value={channel}
                onChange={(value) => {
                  if (value === 'sms' || value === 'email') {
                    setChannel(value);
                  }
                }}
              />
            </View>
          ) : null}

          <View style={[styles.section, { zIndex: 5 }]}>
            <Label name={`Existing ${leadsLabel}`} optional="Optional" />
            <LeadDropdown
              value={selectedLeads}
              setValue={(ids) => setSelectedLeads(Array.isArray(ids) ? ids : ids ? [ids] : [])}
              style={{ zIndex: 5 }}
            />
          </View>

          <View style={[styles.section, { zIndex: 4 }]}>
            <GroupDropdown
              label="Groups"
              optional="Optional"
              value={selectedGroups}
              setValue={(ids: string | string[] | null) =>
                setSelectedGroups(Array.isArray(ids) ? ids : ids ? [ids] : [])
              }
              multiple={true}
              style={{ zIndex: 4 }}
            />
          </View>

          <View style={styles.section}>
            <Label
              name={channel === 'sms' ? 'Extra phone numbers' : 'Extra email addresses'}
              optional="Not already a contact"
            />
            <Info
              info={
                channel === 'sms'
                  ? 'Type or paste numbers that are not already a contact. Commas or new lines are fine. You do not have to select an existing contact.'
                  : 'Type or paste addresses that are not tied to a contact. Commas or new lines are fine. A valid address here is enough — you do not have to select an existing contact.'
              }
            />
            <View style={styles.extraRow}>
              <form
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  addExtraRecipients();
                }}
              >
                <Input
                  key={`extra-draft-${formKey}-${channel}`}
                  name=""
                  style={styles.extraInput}
                  value={extraDraft}
                  setValue={setExtraDraft}
                  placeholder={channel === 'sms' ? '5551234567' : 'name@example.com'}
                  textAlign="left"
                  fontSize={16}
                />
                <LocalButton
                  text="Add"
                  theme={Theme.PRIMARY}
                  width={90}
                  onPress={addExtraRecipients}
                  disabled={!extraDraft.trim()}
                />
              </form>
            </View>
            {renderChips()}
          </View>

          {channel === 'sms' ? (
            <View style={styles.section}>
              <Label name="Message" />
              <MessageEditor
                key={`sms-editor-${formKey}`}
                messageContent={messageContent}
                setMessageContent={setMessageContent}
                setMessage={setMessage}
              />
            </View>
          ) : (
            <View style={styles.section}>
              <Label name="Subject" />
              <Input
                key={`email-subject-${formKey}`}
                name=""
                value={emailSubject}
                setValue={setEmailSubject}
                placeholder="Email subject"
                textAlign="left"
                fontSize={16}
                style={styles.textInput}
              />
              <Label name="Email body" />
              <Input
                key={`email-body-${formKey}`}
                name=""
                value={emailBody}
                setValue={setEmailBody}
                placeholder="Write your email..."
                multiline={true}
                textAlign="left"
                fontSize={16}
                style={styles.textArea}
              />
              <Info info="Email uses the same SendGrid account as password reset and invites. Sending will work once the email API is enabled on the server." />
            </View>
          )}

          <Text style={styles.summary}>{recipientSummary()}</Text>

          {error ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}

          <View style={styles.buttonContainer}>
            <Button
              type="Primary"
              size="Default"
              shape="Pill"
              onClick={onSend}
              disabled={!canSend}
            >
              {loading ? (
                <Spinner color="#FFF" />
              ) : (
                <Text style={styles.sendText}>
                  {channel === 'sms' ? 'Send Text' : 'Send Email'}
                </Text>
              )}
            </Button>
          </View>
        </View>
      </View>

      <WpModal isVisible={showConfirm} closeModal={() => setShowConfirm(false)}>
        <ModalBody title="Messaging Confirmation" closeModal={() => setShowConfirm(false)}>
          <Text style={styles.confirmText}>
            The opt-in message below will be sent to any new {leadsLabel} that have not yet opted-in
            to receive SMS messages from you.
          </Text>
          {state.user?.organization?.message ? (
            <MessageBubble
              userId={'1'}
              message={state.user.organization.message}
              margin={'5%'}
              maxWidth={'90%'}
              backgroundColor={'white'}
            />
          ) : (
            <Text style={styles.confirmText}>
              No opt-in message has been set. You can add one in Account settings.
            </Text>
          )}
          <View style={styles.confirmRow}>
            <Checkbox onSelect={setUnderstandTiming} selected={understandTiming} size={24} />
            <Text style={styles.confirmLabel}>
              I understand that messages are only sent out between 8am-8pm MST. If you try to send a
              message outside of these times, our servers will wait until 8am the following morning
              to send them out.
            </Text>
          </View>
          <View style={styles.confirmRow}>
            <Checkbox onSelect={setUnderstandContacts} selected={understandContacts} size={24} />
            <Text style={styles.confirmLabel}>
              I understand {leadsLabel} I add personally will need to opt-in to receive future
              communication. Any {leadsLabel} added via a {Constants.brand} landing page do not need
              an additional opt-in.
            </Text>
          </View>
          <View style={styles.confirmRow}>
            <Checkbox onSelect={setUnderstandEdit} selected={understandEdit} size={24} />
            <Text style={styles.confirmLabel}>
              I understand I can edit the opt-in message anytime in settings.
            </Text>
          </View>
          <ModalFooter>
            <Button
              disabled={confirmDisabled || loading}
              onClick={performSend}
              shape="Pill"
              size="Default"
              type="Primary"
            >
              {loading ? (
                <Spinner color="#FFF" />
              ) : (
                <Text style={styles.sendText}>Confirm and Send</Text>
              )}
            </Button>
          </ModalFooter>
        </ModalBody>
      </WpModal>

      <BillingModal
        isVisible={isBilling}
        onClose={() => setIsBilling(false)}
        onComplete={onCompleteBilling}
        loading={updateUserData.loading}
        card={state.user?.organization?.billing?.card}
      />
        </>
      )}
    </BasicLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 25,
    maxWidth: 840,
    width: '100%',
  },
  heading: {
    fontSize: 32,
    fontFamily: 'GothamBold',
    color: Constants.colors.blue,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
    marginBottom: 24,
  },
  card: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    padding: 24,
  },
  section: {
    marginBottom: 22,
    position: 'relative',
  },
  extraRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  extraInput: {
    flex: 1,
    marginBottom: 0,
    textAlign: 'left',
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 10,
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Constants.colors.lightBlue,
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  chipText: {
    fontFamily: 'GothamMedium',
    color: Constants.colors.darkBlue,
    marginRight: 6,
    fontSize: 13,
  },
  textInput: {
    textAlign: 'left',
    padding: 12,
    fontSize: 14,
    fontFamily: 'Gotham',
    marginBottom: 16,
  },
  textArea: {
    minHeight: 180,
    textAlign: 'left',
    padding: 15,
    fontSize: 14,
    fontFamily: 'Gotham',
  },
  summary: {
    fontFamily: 'GothamMedium',
    color: Constants.colors.darkBlue,
    marginBottom: 12,
  },
  errorContainer: {
    backgroundColor: '#FEE',
    borderColor: Constants.colors.red,
    borderWidth: 1,
    borderRadius: 5,
    padding: 15,
    marginBottom: 12,
  },
  errorText: {
    color: Constants.colors.red,
    fontFamily: 'Gotham',
    fontSize: 14,
  },
  buttonContainer: {
    marginTop: 8,
    flexDirection: 'row',
  },
  sendText: {
    color: '#FFF',
    fontFamily: 'GothamMedium',
  },
  confirmText: {
    fontFamily: 'GothamMedium',
    color: '#555',
    paddingHorizontal: 10,
    marginBottom: 12,
  },
  confirmRow: {
    marginVertical: 10,
    marginHorizontal: 5,
    flexDirection: 'row',
    alignItems: 'center',
  },
  confirmLabel: {
    fontFamily: 'Gotham',
    flex: 1,
  },
});

const cssStyles = {
  chipIcon: {
    width: 14,
    height: 14,
    color: Constants.colors.darkBlue,
    cursor: 'pointer',
  },
};
